import React, { useState, useEffect } from 'react';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import '../../css/LeaveRequestForm.css';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { faL, faTimesCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import DateInput from '../common/DateInput';

registerPlugin(FilePondPluginImagePreview, FilePondPluginFileValidateType, FilePondPluginFileValidateSize);
const loadingGif = process.env.REACT_APP_PUBLIC_URL + '/loading1.gif';

function UpdateLeaveForm({ leaveData, language, isFormDisabledProp, onUpdateLeave, employeeID, responseMessage, isErrorMessage, setIsParentLoading, setResponseMessage }) {
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        leaveType: leaveData.AbsenceTypeCode || '',
        fromDate: formatDate(leaveData.StartDate) || '',
        toDate: formatDate(leaveData.EndDate) || '',
        notes: leaveData.Note || '',
        attachments: [],
        department: leaveData.Department || '',
        employee: leaveData.Employee || '',
        approver: leaveData.ApproverEmployeeName || { "Name": t("Loading...") },
        hospital_type: leaveData.HospitalType || "",
        hospital_name: leaveData.HospitalName || "",
        hospital_location: leaveData.HospitalLocation === "199", // inside Qatar
    });

    const [errors, setErrors] = useState({});
    const [isFormDisabled, setIsFormDisabled] = useState(isFormDisabledProp);
    const [isLoading, setIsLoading] = useState(true);
    const [showSubstitution, setShowSubstitution] = useState(false);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [attachmentNames, setAttachmentNames] = useState([]);
    const [attachmentFilesTypes, setAttachmentFilesTypes] = useState([
        "application/pdf",
        "image/jpeg",
        "image/tiff",
        "image/gif",
        "image/jpeg",
        "image/tiff"
    ]);
    const [validFiles, setValidFiles] = useState(true);
    const [numOfDays, setNumOfDays] = useState(0);
    const [substitutions, setSubstitutions] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [hospitalTypes, setHospitalTypes] = useState([]);
    const [csrfToken, setCSRFToken] = useState("");
    const [subCSRFToken, setSubCSRFToken] = useState("");
    const [subCookies, setSubCookies] = useState("");
    const [cookies, setCookies] = useState("");
    const [isManager, setIsManager] = useState(false);
    const [loadingCounter, setLoadingCounter] = useState(4); // Start with the number of async operations
    const [isLeaveUpdateLoading, setIsLeaveUpdateLoading] = useState(false);
    const [currentAttachments, setCurrentAttachments] = useState([]);

    function formatDate(dateString) {
        const timestamp = dateString.match(/\d+/)[0];
        const date = new Date(parseInt(timestamp, 10));
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`; // Return the date in YYYY-MM-DD format for input elements
    }

    const decrementLoadingCounter = () => {
        setLoadingCounter(prevCount => {
            const newCount = prevCount - 1;
            if (newCount === 0) {
                setIsLoading(false);  // Only set loading to false when all async operations are done
            }
            return newCount;
        });
    };

    useEffect(() => {
        const extractAttachments = (data) => {
            const attachments = [];
            for (let i = 1; i <= 5; i++) {
                const attachmentKey = `Attachment${i}`;
                const attachment = data[attachmentKey];
                if (attachment && attachment.FileName) {
                    const url = `${process.env.REACT_APP_MAWARED_URL}/sap/opu/odata/sap/HCMFAB_LEAVE_REQUEST_CR_SRV/FileAttachmentSet(EmployeeID='${data.EmployeeID}',LeaveRequestId='${data.RequestID}',ArchivDocId='${attachment.ArchivDocId}',FileName='${attachment.FileName}')/$value`;

                    attachments.push({
                        source: url,
                        options: {
                            type: 'remote',
                            file: {
                                name: attachment.FileName,
                                size: attachment.FileSize || null, // Optionally add size if known
                                type: attachment.MIMEType || 'application/octet-stream', // Use the correct MIME type here
                            },
                            metadata: {
                                id: attachment.ArchivDocId,
                                url: url
                            }
                        }
                    });
                }
            }
            return attachments;
        };

        const fetchLeaveDetails = async () => {
            try {
                let frontendLog = JSON.stringify({
                    "sessionID": localStorage.getItem('sessionID'),
                    "EmployeeID": localStorage.getItem('EmployeeID'),
                    "channel": isMobile ? "Mobile" : "Portal",
                    "language": localStorage.getItem('language')
                });

                const response = await fetch(`${process.env.REACT_APP_MIDDLEWARE_API}/getLeaveDetails?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}&token=${encodeURIComponent(localStorage.getItem('id_token'))}&language=${encodeURIComponent(language)}&EmployeeID=${encodeURIComponent(localStorage.getItem('EmployeeID'))}&frontendLog=${encodeURIComponent(frontendLog)}&RequestID=${leaveData.RequestID}&ChangeStateID=${leaveData.ChangeStateID}&LeaveKey=${leaveData.LeaveKey}`);

                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const jsonResponse = await response.json();
                const data = jsonResponse.data;
                // Extract the attachments
                setCurrentAttachments(extractAttachments(data));
                setFormData((prevFormData) => ({
                    ...prevFormData,
                    // attachments: attachments
                }));

            } catch (error) {
                console.error('Error fetching leave details:', error);
            } finally {
                decrementLoadingCounter();  // Decrement the counter after this operation completes
            }
        };

        fetchLeaveDetails();
    }, [leaveData, isMobile, language]);

    useEffect(() => {
        setIsFormDisabled(isFormDisabledProp);
    }, [isFormDisabledProp]);

    useEffect(() => {
        checkDateDifference(formData.fromDate, formData.toDate);
    }, [formData.fromDate, formData.toDate]);

    const checkDateDifference = (fromDate, toDate) => {
        const from = new Date(fromDate);
        const to = new Date(toDate);
        const timeDiff = to - from;
        const dayDiff = timeDiff / (1000 * 3600 * 24);
        setNumOfDays(dayDiff + 1 + " " + t('Days')); // Including the start date
        setShowSubstitution(dayDiff > 2); // 4 days or more
    };
    function convertToCookieString(jsonObject) {
        const cookieArray = [];
        for (const [key, value] of Object.entries(jsonObject)) {
            cookieArray.push(`${key}=${value}`);
        }
        return cookieArray.join('; ');
    }

    useEffect(() => {
        const fetchLeaveTypes = async () => {
            try {
                let frontendLog = JSON.stringify({ "sessionID": localStorage.getItem('sessionID'), "EmployeeID": localStorage.getItem('EmployeeID'), "channel": isMobile ? "Mobile" : "Portal", "language": localStorage.getItem('language') })
                const response = await fetch(`${process.env.REACT_APP_MIDDLEWARE_API}/getAllLeaveTypes?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}&token=${encodeURIComponent(localStorage.getItem('id_token'))}&language=${encodeURIComponent(language)}&EmployeeID=${encodeURIComponent(localStorage.getItem('EmployeeID'))}&frontendLog=${encodeURIComponent(frontendLog)}`);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();
                setLeaveTypes(data);
                setCSRFToken(data[0].csrfToken);
                setCookies(convertToCookieString(data[0].cookies));
                // Check if the initial leave type has attachments and update state
                const initialLeaveType = data.find(type => type.AbsenceTypeCode === leaveData.AbsenceTypeCode);
                if (initialLeaveType && initialLeaveType.Attachments) {
                    //   updateAttachmentNames(initialLeaveType);
                }
                if (initialLeaveType && initialLeaveType.Approver) {
                    setFormData(prevFormData => ({
                        ...prevFormData,
                        approver: initialLeaveType.Approver // Set the approver
                    }));
                }
            } catch (error) {
                console.error('Error fetching leave types:', error);
            } finally {
                decrementLoadingCounter();  // Decrement the counter after this operation completes
            }
        };

        fetchLeaveTypes();
    }, [employeeID]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                let frontendLog = JSON.stringify({ "sessionID": localStorage.getItem('sessionID'), "EmployeeID": localStorage.getItem('EmployeeID'), "channel": isMobile ? "Mobile" : "Portal", "language": localStorage.getItem('language') })
                const response = await fetch(`${process.env.REACT_APP_MIDDLEWARE_API}/getCurrentEmployee?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}&token=${encodeURIComponent(localStorage.getItem('id_token'))}&sessionID=${encodeURIComponent(localStorage.getItem('sessionID') + "__" + localStorage.getItem('EmployeeID'))}&frontendLog=${encodeURIComponent(frontendLog)}`);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const result = await response.json();
                localStorage.setItem("EmployeeID", result.EmployeeID);
                localStorage.setItem("IsManager", result.IsManager);
                setIsManager(result.IsManager == "true" ? true : false);
                fetchDepartments(result.EmployeeID);
            } catch (error) {
                console.error(error);
            }
        };

        const fetchDepartments = async (currentEmployeeID) => {
            try {
                let frontendLog = JSON.stringify({ "sessionID": localStorage.getItem('sessionID'), "EmployeeID": localStorage.getItem('EmployeeID'), "channel": isMobile ? "Mobile" : "Portal", "language": localStorage.getItem('language') })
                const response = await fetch(`${process.env.REACT_APP_MIDDLEWARE_API}/getDepartment?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}&EmployeeID=${encodeURIComponent(currentEmployeeID)}&token=${encodeURIComponent(localStorage.getItem('id_token'))}&language=${encodeURIComponent(language)}&frontendLog=${encodeURIComponent(frontendLog)}`);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();
                setDepartments(data.data);
                setSubCSRFToken(data.csrfToken);
                setSubCookies(convertToCookieString(data.cookies));
                fetchEmployeesInDepartment(data.data[0].Department)
            } catch (error) {
                console.error('Error fetching departments:', error);
            } finally {
                decrementLoadingCounter();  // Decrement the counter after this operation completes
            }
        };

        fetchData();
    }, []);

    const fetchEmployeesInDepartment = async (departmentID) => {
        try {
            let frontendLog = JSON.stringify({ "sessionID": localStorage.getItem('sessionID'), "EmployeeID": localStorage.getItem('EmployeeID'), "channel": isMobile ? "Mobile" : "Portal", "language": localStorage.getItem('language') })
            const response = await fetch(`${process.env.REACT_APP_MIDDLEWARE_API}/getEmployeeInDepartment?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}&DepartmentID=${encodeURIComponent(departmentID)}&token=${encodeURIComponent(localStorage.getItem('id_token'))}&language=${encodeURIComponent(language)}&sessionID=${encodeURIComponent(localStorage.getItem('sessionID') + "__" + localStorage.getItem('EmployeeID'))}&frontendLog=${encodeURIComponent(frontendLog)}`);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            setSubstitutions(data.data);
            setFormData(prevFormData => ({
                ...prevFormData,
                department: departmentID,
            }));
        } catch (error) {
            console.error('Error fetching employees:', error);
        } finally {
            decrementLoadingCounter();  // Decrement the counter after this operation completes
        }
    };

    const handleFilePondUpdate = (fileItems) => {
        const files = fileItems.map(fileItem => fileItem.file);

        // Check if all files are of accepted types
        const allValid = files.every(file => attachmentFilesTypes.includes(file.type));
        setValidFiles(allValid);

        setFormData((prevFormData) => ({
            ...prevFormData,
            attachments: files
        }));

        // Set error if any invalid file is uploaded
        if (!allValid) {
            setErrors(prevErrors => ({
                ...prevErrors,
                attachments: t("One or more files are not in the correct format.")
            }));
        } else {
            setErrors(prevErrors => ({ ...prevErrors, attachments: "" }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prevFormData) => {
            // If the leaveType field is changed, find the corresponding approver
            if (name === 'leaveType') {
                const selectedLeaveType = leaveTypes.find(type => type.AbsenceTypeCode === value);
                const newApprover = selectedLeaveType ? selectedLeaveType.Approver : t("Loading...");
                return {
                    ...prevFormData,
                    [name]: value,
                    approver: newApprover, // Update the approver field based on leave type
                };
            } else if (name === 'department') {
                if (value) {
                    fetchEmployeesInDepartment(value);
                } else {
                    setSubstitutions([])
                }
            }

            // For other fields, just update them normally
            return {
                ...prevFormData,
                [name]: value,
            };
        });
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));
        checkDateDifference(formData.fromDate, formData.toDate);
    };

    const validate = () => {
        setResponseMessage("")
        let tempErrors = {};
        if (!formData.fromDate) tempErrors.fromDate = t("From date is required.");
        if (!formData.toDate) tempErrors.toDate = t("To date is required.");
        if (new Date(formData.fromDate) > new Date(formData.toDate))
            tempErrors.date = t("From date cannot be later than to date.");
        if (attachmentNames.length > 0 && formData.attachments.length !== attachmentNames.length)
            tempErrors.attachments = t("Please upload {{count}} attachment(s).", { count: attachmentNames.length });
        if (!validFiles) {
            tempErrors.attachments = t("Invalid file type. Please upload PDF, JPEG, TIFF, or GIF files.");
        }
        if (showSubstitution) {
            if (!formData.department) tempErrors.department = t("Department is required for leaves of 4 days or more.");
            if (!formData.employee) tempErrors.employee = t("Substitution is required");
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLeaveUpdateLoading(true);

        try {
            if (validate()) {
                // Create an object with only the updated values
                const updatedData = {};
                for (const key in formData) {
                    if (formData[key] !== leaveData[key]) {
                        updatedData[key] = formData[key];
                    }
                }
                // Handle the update logic
                await onUpdateLeave(updatedData, csrfToken, cookies, leaveData, showSubstitution, subCSRFToken, subCookies, substitutions, currentAttachments);
            }
        } catch (error) {
            setIsLeaveUpdateLoading(false);
            console.error('Error updating leave:', error);
        } finally {
            setIsLeaveUpdateLoading(false);
        }
    };
    const deleteAttachment = async (attachment, event) => {
        setIsLeaveUpdateLoading(true);
        const closestTableContainer = event.target.closest('.chat-body');
        let frontendLog = JSON.stringify({
            "sessionID": localStorage.getItem('sessionID'),
            "EmployeeID": localStorage.getItem('EmployeeID'),
            "channel": isMobile ? "Mobile" : "Portal",
            "language": localStorage.getItem('language')
        });
        // Show confirmation dialog before deleting
        const result = await Swal.fire({
            text: t('Are you sure you want to delete ') + attachment.options.file.name,
            // icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#891638',
            cancelButtonColor: '#fff',
            confirmButtonText: t('Yes'),
            cancelButtonText: t('Cancel'),
            position: 'bottom',
            customClass: {
                popup: 'custom-swal-size', // custom class for custom styling
                confirmButton: 'custom-confirm-button', // custom class for confirm button
                cancelButton: 'custom-cancel-button',
            },
            target: closestTableContainer,
            backdrop: 'rgba(0, 0, 0, 0)',
        });

        if (result.isConfirmed) {
            setIsParentLoading(true)
            try {
                // Find the matching attachment in leaveData by ArchivDocId
                let matchingAttachmentKey = null;
                let matchingAttachment = null;

                // Iterate through all possible attachment fields
                for (let i = 1; i <= 5; i++) {
                    const attachmentKey = `Attachment${i}`;
                    const currentAttachment = leaveData[attachmentKey];

                    if (currentAttachment && currentAttachment.ArchivDocId === attachment.options.metadata.id) {
                        matchingAttachmentKey = attachmentKey;
                        matchingAttachment = currentAttachment;
                        break; // Exit loop once we find the matching attachment
                    }
                }

                if (matchingAttachment) {
                    // Prepare the updated attachment object with AttachmentStatus set to "D"
                    const updatedAttachment = {
                        "__metadata": {
                            "type": "HCMFAB_LEAVE_REQUEST_CR_SRV.Attachment"
                        },
                        "Pernr": leaveData.EmployeeID,
                        "LeaveRequestId": leaveData.RequestID,
                        "FilePath": matchingAttachment.FilePath,
                        "FileName": matchingAttachment.FileName,
                        "FileType": matchingAttachment.FileType,
                        "FileSizeDescr": matchingAttachment.FileSizeDescr,
                        "FileSize": matchingAttachment.FileSize,
                        "FileUrl": matchingAttachment.FileUrl,
                        "DeleteEnabled": matchingAttachment.DeleteEnabled,
                        "CreaUser": matchingAttachment.CreaUser,
                        "CreaDate": matchingAttachment.CreaDate,
                        "CreaTime": matchingAttachment.CreaTime,
                        "ArchivDocId": matchingAttachment.ArchivDocId,
                        "AttachmentStatus": "D",  // Set status to "D" for delete
                        "FileSeqnr": matchingAttachment.FileSeqnr,
                        "Action": ""
                    };

                    // Construct the request payload
                    const payload = {
                        "AdditionalFields": leaveData.AdditionalFields,
                        [matchingAttachmentKey]: updatedAttachment
                    };

                    // Your API call to update the leave request with the updated attachment
                    const response = await fetch(`${process.env.REACT_APP_MIDDLEWARE_API}/deleteAttachments?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}&token=${encodeURIComponent(localStorage.getItem('id_token'))}&language=${encodeURIComponent(language)}&EmployeeID=${encodeURIComponent(localStorage.getItem('EmployeeID'))}&frontendLog=${encodeURIComponent(frontendLog)}&RequestID=${leaveData.RequestID}&ChangeStateID=${leaveData.ChangeStateID}&LeaveKey=${leaveData.LeaveKey}&csrfToken=${csrfToken}&cookies=${encodeURIComponent(cookies)}`, {
                        method: 'POST',
                        body: JSON.stringify(payload)
                    });

                    if (response.ok) {
                        // Show success alert
                        Swal.fire({
                            title: t('Deleted!'),
                            // icon: 'success',
                            confirmButtonText: t('OK'),
                            position: 'bottom',
                            customClass: {
                                popup: 'custom-swal-size', // custom class for custom styling
                            },
                            target: closestTableContainer,
                            backdrop: 'rgba(0, 0, 0, 0)',
                        });

                        // Remove the deleted attachment from currentAttachments
                        setCurrentAttachments((prevAttachments) =>
                            prevAttachments.filter((item) => item.source !== attachment.source)
                        );
                    } else {
                        throw new Error('Failed to update the attachment status');
                    }
                } else {
                    throw new Error('No matching attachment found');
                }
            } catch (error) {
                // Show error alert
                Swal.fire({
                    title: t('Error!'),
                    text: t('There was an error occurred, Please try again.'),
                    // icon: 'error',
                    confirmButtonText: t('OK'),
                    position: 'bottom',
                    customClass: {
                        popup: 'custom-swal-size', // custom class for custom styling
                    },
                    target: closestTableContainer,
                    backdrop: 'rgba(0, 0, 0, 0)',
                });
                console.error('Error deleting attachment:', error);
            } finally {
                setIsLeaveUpdateLoading(false);
                setIsParentLoading(false)
            }
        } else {
            setIsLeaveUpdateLoading(false);
            setIsParentLoading(false)
        }
    };




    return (
        <>
            {(isLoading) ? (
                <div className='centered-v-h'>
                    {/* <img src={`${process.env.REACT_APP_PUBLIC_URL}/loading1.gif`} alt="Loading..." className="loading-gif" /> */}
                    <img src={loadingGif} alt="Loading..." className="loading-gif" width={50} height={50} />
                </div>
            ) : (
                <>
                    <div className="leave-type-select">
                        <label className="form-label">{t('Leave Type')}</label>
                        <select
                            name="leaveType"
                            value={formData.leaveType}
                            onChange={handleChange}
                            className="form-control small-font"
                            disabled={isFormDisabled}
                        >
                            {leaveTypes.map((type) => (
                                <option key={type.AbsenceTypeCode} value={type.AbsenceTypeCode}>
                                    {type.AbsenceTypeName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-card">
                        <div className="form-card-title">
                            {t('When will you be on vacation?')}
                        </div>
                        <form onSubmit={handleSubmit} className="w-100">
                            <div className="mb-3">
                                <label className="form-label">{t('From Date')}</label>
                                <DateInput
                                    name="fromDate"
                                    value={formData.fromDate}
                                    onChange={handleDateChange}
                                    disabled={isFormDisabled}
                                    placeholder={t('Select from date')}
                                />
                                {errors.fromDate && <span className="error">{errors.fromDate}</span>}
                            </div>
                            <div className="mb-3">
                                <label className="form-label">{t('To Date')}</label>
                                <DateInput
                                    name="toDate"
                                    value={formData.toDate}
                                    onChange={handleDateChange}
                                    disabled={isFormDisabled}
                                    placeholder={t('Select to date')}
                                />
                                {errors.toDate && <span className="error">{errors.toDate}</span>}
                            </div>
                            <div className="mb-3">
                                <label className="form-label">{t('Number of Days')}</label>
                                <div className="num-of-days">
                                {numOfDays}
                                </div>
                            </div>
                            {/* Add more fields as needed, similar to LeaveRequestForm */}
                            {showSubstitution && (
                                <div className="flex-row">
                                    <div className="inline-form mb-3">
                                        <label className="form-label">{t('Substitution')}</label>
                                        <div>
                                            <select
                                                name="department"
                                                className="form-control small-font"
                                                value={formData.department}
                                                onChange={handleChange}
                                                disabled={isFormDisabled}
                                            >
                                                <option value="">{t('Select a department')}</option>
                                                {departments.map((department) => (
                                                    <option key={department.Department} value={department.Department}>
                                                        {department.Name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <select
                                                name="employee"
                                                className="form-control small-font"
                                                value={formData.employee}
                                                onChange={handleChange}
                                                disabled={isFormDisabled}
                                            >
                                                <option value="">{t('Select an employee')}</option>
                                                {substitutions.map((employee) => (
                                                    <option key={employee.EmpNum} value={employee.EmpNum}>
                                                        {employee.EmpName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="mb-3">
                                <label className="form-label">{t('Approver')}</label>
                                <input
                                    type="text"
                                    name="approver"
                                    className="form-control small-font"
                                    value={formData?.approver.Name}
                                    disabled
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">{t('Notes')}</label>
                                <textarea
                                    name="notes"
                                    className="form-control small-font"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    disabled={isFormDisabled}
                                />
                                {errors.notes && <span className="error">{errors.notes}</span>}
                            </div>
                            {attachmentNames.length > 0 && (
                                <div className="mb-3 attachment-container">
                                    <label className="mb-2 attachment-name">{t('Attachments Required')}</label>
                                    {attachmentNames.map((attachmentName, index) => (
                                        <p
                                            key={index}
                                            type="text"
                                            className="form-control small-font small-font"
                                            disabled
                                        >
                                            {attachmentName}
                                        </p>
                                    ))}
                                </div>
                            )}
                            <label className="form-label">{t('Attachments')}</label>
                            <div>
                                {currentAttachments.map((attachment, index) => (
                                    <div key={index} className="attachment-item">
                                        <a href={attachment.source.startsWith('http') ? attachment.source : `https://${attachment.source}`} className='attachment-link' target='_blank'>
                                            {attachment.options.file.name}
                                        </a>
                                        <FontAwesomeIcon
                                            className={`delete ${isFormDisabled ? 'disabled' : ''}`}
                                            icon={faTrash}
                                            onClick={(event) => !isFormDisabled && deleteAttachment(attachment, event)}
                                        />
                                    </div>
                                ))}
                                <br></br>

                            </div>
                            <div className="mb-3">
                                <FilePond
                                    files={formData.attachments}
                                    onupdatefiles={handleFilePondUpdate}
                                    allowMultiple={true}
                                    maxFiles={10}
                                    name="attachments"
                                    disabled={isFormDisabled}
                                    acceptedFileTypes={attachmentFilesTypes}
                                    labelIdle={`<span class='large-font'> + </span> ${t('Drag & Drop your files or')} <span class="filepond--label-action">${t('Browse')}</span>`}
                                />
                                {errors.attachments && <span className="error">{errors.attachments}</span>}
                            </div>
                            {responseMessage && <span className={isErrorMessage ? 'red-span' : 'green-span'}>{responseMessage}</span>}
                            <div className="substitution-error">
                                {errors.employee && <span className="error">{errors.employee}</span>}
                            </div>
                            <div className="button-group">
                                <button className="submit-button" type="submit" disabled={isFormDisabled || isLoading}>
                                    {t('Save')}
                                </button>
                                <button className="cancel-button" type="button" disabled={isFormDisabled} onClick={() => onUpdateLeave('cancel')}>
                                    {t('Cancel')}
                                </button>
                            </div>
                        </form>
                    </div>
                </>)
            }
        </>
    )
}

export default UpdateLeaveForm;
